import { BasketCategoryModel } from '@agent/core/basket/basket.model';
import { IBasketStore } from '@agent/core/basket/basket.store';
import { BasketCategoryType, BasketResult, Sort } from '@agent/core/basket/models/basket';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { EpticaRequestType, MailRequest } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ISearchService } from '@agent/core/search/search.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { MailComment } from '@commons/history/history-mail/mail-comment/models/mail-comment';
import { ChannelUtil } from '@commons/util/helper/channel';
import { deepCopy } from '@commons/util/helper/helper';
import { destroy } from '@commons/util/utilities/rx';
import { ILogService } from 'angular';
import { isDate } from 'moment';
import { catchError, combineLatest, filter, from, map, mergeMap, Observable, of, startWith, switchMap, take, tap, toArray } from 'rxjs';

import {
  AgentMailFacade,
  AgentUserFacade,
  BookingStatus,
  capitalize,
  ChanEnum,
  ChannelType,
  Chosen,
  Configuration,
  CSR_FACADE_PREFIX,
  ElasticSearchService,
  EpticaExposedDraft,
  EpticaExposedRequestEvent,
  EpticaRequestEventType,
  EpticaRequestState,
  EpticaService,
  extractEpticaField,
  extractLastRequestEventDtoByType,
  extractLastRequestEventType,
  isNotNullOrUndefined,
  Logger,
  MAIL_FACADE_PREFIX,
  MailService,
  MediaSkill,
  Memo,
  ProfileFacade,
  Skill,
  sumOfArr,
} from '@contact/upgrade';

import { fromMailBasket, MailBasketAction } from '../';
import { MailChannelModel, QueryFilterKeys, QueryFilterModel, RequestModel, SearchDto } from './mail-basket.model';
import {
  AttachmentFilter,
  BasketFilter,
  BookmarkFilter,
  DomainFilter,
  PeriodFilter,
  SendPeriodFilter,
  PriorityFilter,
  ReasonFilter,
  RecipientFilter,
  RequestIdFilter,
  SenderFilter,
  SkillsFilter,
  SubjectFilter,
  TeamQuickFilter,
} from './models/basket-filter';
import { QueryRequest } from './models/basket-request';

const MODULE_NAME = 'mailBasketService';
const EXCLUDED_FILTER_PROPS = ['hasFilters'];

export type BasketCounter = [number, EpticaRequestType];
export type MailBasketMap = { [key: string]: any };
export type MailBasketFilterDto = MailBasketMap;
export type MailBasketSortDto = {
  readonly category: string;
  readonly sort: MailBasketMap;
};
export type MailBasketSortAndFilterDto = {
  readonly filterValue: MailBasketFilterDto;
  readonly sortValue: MailBasketSortDto[];
};
const LABELS_FOR_DESKTOP = [EpticaRequestType.APPROVAL_IN_PROCESS, EpticaRequestType.APPROVAL_REJECTED];
const LABELS_FOR_SUP = [EpticaRequestType.APPROVAL];
const LABELS_SHARED = [
  EpticaRequestType.ASK_FOR_HELP_RECEIVED,
  EpticaRequestType.ASK_FOR_HELP_SENT,
  EpticaRequestType.ASK_FOR_HELP_ANSWERED,
  EpticaRequestType.DEDICATED_AGENT_UNREAD,
  EpticaRequestType.RELATED,
  EpticaRequestType.TRANSFERED,
];

export interface IMailBasketService {
  /**
   * ask to business to open request
   */
  openRequest(request: RequestModel, category: BasketCategoryType): Observable<any>;
  /**
   * load requests basket with given configuration
   */
  getRequests(query: QueryRequest, accountId?: string, accountGroupPath?: string): Observable<BasketResult<RequestModel>>;
  /**
   * compute counter when user is not on basket view
   */
  globalCounter(): Observable<BasketResult>;
  /**
   * compute counter for basket view
   * basketCounter(mail: MailChannelModel, modeType: AgentMode): Observable<BasketResult>;
   */
  basketCounter(mail: MailChannelModel): Observable<BasketResult>;
  /**
   *  compute total counter
   */
  getTotalCounter(): Observable<number>;
  /**
   * get nb of mails for the csr's basket
   */
  getTotalCounterForCsr();
  /**
   * Save mail basket filters
   * @param filters to save
   */
  saveFilters(filters: Partial<QueryFilterModel>): Observable<void>;
  /**
   * Save mail basket sort for each categories
   * @param categories containing sort configurations to save
   */
  saveSort(categories: BasketCategoryModel[]): Observable<void>;
  /**
   * Get mail basket filters and sort
   */
  getFiltersAndSort(): Observable<MailBasketSortAndFilterDto>;
  /**
   * Override mail initial state with filters and sort data from result
   * @param mailChannelModel default mailChannelModel values
   * @param result contains data to override
   */
  overrideMailInitialState(mailChannelModel: MailChannelModel, result: MailBasketSortAndFilterDto): MailChannelModel;

  /**
   * Change the bookmark of a request
   * @param requestId request id
   * @param bookmark
   */
  changeBookmark(requestId: string, bookmark: boolean): Observable<void>;
}

export class MailBasketService implements IMailBasketService {
  static $inject: Array<string> = [
    'httpDeprecated',
    'toastDeprecatedService',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'sessionDeprecatedService',
    'basketStore',
    'agentMailFacade',
    'configuration',
    'profileFacade',
    'agentUserFacade',
    'logger',
    'epticaService',
    'mailService',
    'elasticSearchService',
    'search',
  ];

  public name = MODULE_NAME;
  private userId: string;
  private availableSkills: Chosen<Skill>[];
  private priorityFilter = new PriorityFilter();

  constructor(
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly basketStore: IBasketStore,
    private readonly agentMailFacade: AgentMailFacade,
    private readonly configuration: Configuration,
    private readonly profileFacade: ProfileFacade,
    private agentUserFacade: AgentUserFacade,
    private logger: Logger,
    private readonly epticaService: EpticaService,
    private mailService: MailService,
    private readonly elasticSearchService: ElasticSearchService,
    protected readonly searchService: ISearchService,
    private $log: ILogService
  ) {
    let commonBasketDomainList: string[] = this.configuration.basket?.commonBasketDomainList || [];
    let domainName = this.sessionDeprecatedService.getProfile().domainName;
    // Open common basket if domain is in configuration.json domains list
    // TODO bug here if agent has only visio
    commonBasketDomainList
      .reduce<Array<string>>((result, domain) => (result.some(d => d === domain) ? result : [...result, domain]), [])
      .filter(domain => domain.toLowerCase() === domainName.toLowerCase())
      .forEach(() => this.basketStore.dispatch(new MailBasketAction.ChangeCategory(BasketCategoryType.COMMON)));

    // TODO use store
    // contact state event
    this.contactDeprecatedService
      .contactState$()
      .pipe(
        filter(event => event.contact.channel.type === ChannelType.MAIL && event.contact.fromBasket),
        filter(event => event.state === BookingStatus.ALLOCATED)
      )
      .subscribe(() => this.basketStore.dispatch(new MailBasketAction.SuccessOpenRequest()));

    // TODO use store
    // contact state event
    this.contactDeprecatedService
      .contactState$()
      .pipe(
        filter(event => event.contact.channel.type === ChannelType.MAIL && event.contact.fromBasket),
        filter(event => event.state === BookingStatus.CLOSED)
      )
      .subscribe(() => this.basketStore.dispatch(new MailBasketAction.ManualRefresh()));

    // retrieve user id
    this.profileFacade.id$.subscribe(id => (this.userId = id));
    // available skills
    this.agentUserFacade.skills$
      .pipe(
        map((skills: MediaSkill[]) =>
          skills
            .filter(({ channel }) => ChannelUtil.equals(channel, ChanEnum.In.Mail))
            .map(({ skill }) =>
              deepCopy({
                ...skill,
                chosen: false,
                selected: false,
                displayed: true,
              })
            )
        ),
        destroy(this)
      )
      .subscribe(skills => {
        this.availableSkills = skills;
      });

    // priorities
    this.priorityFilter.buildPriorities({
      normal: this.configuration.mail?.priorityRange?.normal,
      medium: this.configuration.mail?.priorityRange?.medium,
    });

    this.logger = logger.forSource(`MailBasketService`);
  }

  public openRequest(request: RequestModel, basketType: BasketCategoryType): Observable<any> {
    return this.epticaDeprecatedService.checkProcessing$(
      request.id,

      this.httpDeprecated
        .asObservable()
        .withData({
          basketType,
          requestTypes: request.types,
        })
        .post(`${CSR_FACADE_PREFIX}/openBasketRequest`, [request.id])
        .pipe(
          tap(null, e => {
            if (e.status === 409) {
              this.toastDeprecatedService.alert('agent:toast.basket_request_already_open');
            } else {
              this.toastDeprecatedService.alert('agent:toast.basket_request_error');
            }
          })
        )
    );
  }

  /**
   * Main method used for getting mails to display within mail baskets.
   */
  public getRequests({
    label,
    currentPage,
    pageSize,
    sort,
    queryFilters,
    quickFilters,
    categorySelected,
  }: QueryRequest): Observable<BasketResult<RequestModel>> {
    // instanciate filter object
    let filters: BasketFilter[] = [
      new SubjectFilter(this.searchService, queryFilters.subject),
      new DomainFilter(queryFilters.domain),
      new RequestIdFilter(queryFilters.requestId),
      new RecipientFilter(queryFilters.recipient),
      new ReasonFilter(queryFilters.reason),
      new SkillsFilter(queryFilters.skills),
      new AttachmentFilter(queryFilters.attachmentEnabled, queryFilters.attachmentActive),
      this.priorityFilter.setValues(queryFilters.priorities),
      new PeriodFilter(queryFilters.beginTime, queryFilters.endTime, queryFilters.isPeriodFilterEnabled),
      new BookmarkFilter(queryFilters.bookmark),
    ];

    switch (categorySelected) {
      case BasketCategoryType.PERSONAL_FOR_CSR:
        return this.getRequestsForPersonalCsr(
          queryFilters,
          sort,
          currentPage,
          pageSize,
          label,
          filters,
          quickFilters.supervisedCsrId,
          quickFilters.supervisedTeamId
        );
      case BasketCategoryType.DELEGATED:
        return this.getRequestsForPersonalCsr(queryFilters, sort, currentPage, pageSize, label, filters, quickFilters.delegatorCsrId);
      case BasketCategoryType.DRAFT:
        return this.getRequestsWithDraft(queryFilters, label, quickFilters.supervisedCsrId);
      case BasketCategoryType.SENT:
        filters.push(new SendPeriodFilter(quickFilters.beginTime, quickFilters.endTime, quickFilters.isSendPeriodFilterEnabled));
        return this.getRequestsFromEptica(
          queryFilters,
          quickFilters.hasFilters,
          sort,
          label,
          currentPage,
          pageSize,
          filters,
          categorySelected
        );
      case BasketCategoryType.COMMON:
        // supervisors have access to a team quick filter in the common basket, so we push it to the actual filters if the teamId is set.
        if (quickFilters.teamId) filters.push(new TeamQuickFilter(quickFilters.teamId));
        return this.getRequestsFromEptica(
          queryFilters,
          quickFilters.hasFilters,
          sort,
          label,
          currentPage,
          pageSize,
          filters,
          categorySelected
        );
      default:
        return this.getRequestsFromEptica(
          queryFilters,
          quickFilters.hasFilters,
          sort,
          label,
          currentPage,
          pageSize,
          filters,
          categorySelected
        );
    }
  }

  private getRequestsFromEptica(
    queryFilters: QueryFilterModel,
    hasQuickFilter: boolean,
    sort: Sort,
    label: string,
    currentPage: number,
    pageSize: number,
    filters: BasketFilter[],
    categorySelected: string
  ): Observable<BasketResult<RequestModel>> {
    // request eptica service
    return this.epticaDeprecatedService
      .getRequests(
        sort.key || sort.defaultKey,
        sort.order,
        EpticaRequestType[label],
        (currentPage - 1) * pageSize,
        pageSize,
        filters.filter(f => !f.isEmpty()).map(f => f.build())
      )
      .pipe(
        switchMap(result =>
          (result && result.data && result.data.length > 0 && BasketCategoryType.PERSONAL === categorySelected
            ? this.epticaService.getDrafts()
            : of([])
          ).pipe(
            switchMap(drafts =>
              from(result.data).pipe(
                map(request => this.toBasketRequest(request, drafts)),
                toArray(),
                map(requests => ({
                  label,
                  filtered: queryFilters.hasFilters || hasQuickFilter,
                  nbRequests: result.total,
                  requests,
                }))
              )
            )
          )
        )
      );
  }

  private getRequestsWithDraft(queryFilters: QueryFilterModel, label: string, csrId: string): Observable<BasketResult<RequestModel>> {
    return this.epticaDeprecatedService.getRequestDrafts().pipe(
      mergeMap(result =>
        from(result.data).pipe(
          filter(request => request.detail.requestState !== EpticaRequestState.closed),
          map(request => this.toBasketRequest(request, [], csrId)),
          toArray(),
          map(requests => ({
            label,
            filtered: queryFilters.hasFilters,
            nbRequests: requests.length,
            requests,
          }))
        )
      )
    );
  }

  private getSearchDtos(csrId?: string, teamId?: string): Observable<SearchDto[]> {
    if (!csrId && teamId) {
      return this.elasticSearchService.searchUser({ teamId }).pipe(
        take(1),
        map(({ data }) =>
          data
            .filter(
              ({ uuid, functionsNames, mailEpticaUserId }) =>
                mailEpticaUserId && (uuid !== this.sessionDeprecatedService.getProfile().id || functionsNames.indexOf('CSR') < 0)
            )
            .map(({ uuid, mailEpticaUserId, groupHierarchy }) => ({
              csrUuid: uuid,
              accountId: mailEpticaUserId,
              accountGroupPath: groupHierarchy,
            }))
        )
      );
    } else if (!csrId && !teamId) {
      return of([]);
    } else {
      return this.elasticSearchService.searchOneUser({ id: csrId }).pipe(
        take(1),
        isNotNullOrUndefined(),
        map(({ mailEpticaUserId, groupHierarchy }) => ({
          csrUuid: csrId || '',
          accountId: mailEpticaUserId,
          accountGroupPath: groupHierarchy,
        })),
        toArray()
      );
    }
  }

  private getRequestsForPersonalCsr(
    queryFilters: QueryFilterModel,
    sort: Sort,
    currentPage: number,
    pageSize: number,
    label: string,
    filters: BasketFilter[],
    csrId?: string,
    teamId?: string
  ): Observable<BasketResult<RequestModel>> {
    const emptyResult = of({ label, filtered: queryFilters.hasFilters, nbRequests: 0, requests: [] });
    const searchDtosList: Observable<SearchDto[]> = this.getSearchDtos(csrId, teamId || '');
    return searchDtosList.pipe(
      switchMap(searchDtos => {
        if (!searchDtos) {
          return emptyResult;
        }

        // do the mail search
        return this.epticaDeprecatedService
          .getRequestsOfCsrs(
            searchDtos,
            sort.key || sort.defaultKey,
            sort.order,
            EpticaRequestType.RELATED,
            (currentPage - 1) * pageSize,
            pageSize,
            filters.filter(f => !f.isEmpty()).map(f => f.build())
          )
          .pipe(
            mergeMap(result =>
              from(result.data).pipe(
                map(request => this.toBasketRequest(request, [], csrId)),
                toArray(),
                map(requests => ({
                  label,
                  filtered: queryFilters.hasFilters,
                  nbRequests: result.total,
                  requests,
                }))
              )
            )
          );
      })
    );
  }

  public globalCounter(): Observable<BasketResult> {
    const type = [EpticaRequestType.ASSIGNABLE, EpticaRequestType.DEDICATED_AGENT_UNASSIGNED, EpticaRequestType.RELATED];
    return this.getCounterList(type);
  }

  public basketCounter(mail: MailChannelModel): Observable<BasketResult> {
    if (mail.categorySelected === BasketCategoryType.PERSONAL) {
      return this.counterForPersonalCategory(mail);
    } else {
      return this.counterForOtherCategories(mail);
    }
  }

  private counterForPersonalCategory(mail: MailChannelModel): Observable<BasketResult> {
    let type = [
      // add shared counter
      ...LABELS_SHARED.filter(request => MailBasketService.getCounterChecked(mail, request)),
      // add counters depends on mode
      ...(this.sessionDeprecatedService.isSupFeatureEnabled
        ? LABELS_FOR_SUP.filter(request => MailBasketService.getCounterChecked(mail, request))
        : LABELS_FOR_DESKTOP.filter(request => MailBasketService.getCounterChecked(mail, request))),
    ];
    if (MailBasketService.getCounterChecked(mail, EpticaRequestType.ASSIGNABLE)) {
      type = [...type, EpticaRequestType.ASSIGNABLE];
    }
    if (MailBasketService.getCounterChecked(mail, EpticaRequestType.DEDICATED_AGENT_UNASSIGNED)) {
      type = [...type, EpticaRequestType.DEDICATED_AGENT_UNASSIGNED];
    }
    return this.getCounterList(type);
  }

  private counterForOtherCategories(mail: MailChannelModel): Observable<BasketResult> {
    let type = [EpticaRequestType.RELATED];
    if (MailBasketService.getCounterChecked(mail, EpticaRequestType.ASSIGNABLE)) {
      type = [...type, EpticaRequestType.ASSIGNABLE];
    }
    if (MailBasketService.getCounterChecked(mail, EpticaRequestType.DEDICATED_AGENT_UNASSIGNED)) {
      type = [...type, EpticaRequestType.DEDICATED_AGENT_UNASSIGNED];
    }
    return this.getCounterList(type);
  }

  private static getCounterChecked(mail: MailChannelModel, requestType: EpticaRequestType): boolean {
    return mail.labelSelected !== requestType || mail.queryFilters.hasFilters;
  }

  private getCounterList(requestsType: string[]): Observable<BasketResult> {
    return this.epticaDeprecatedService.getSearchRequestCount(requestsType).pipe(
      mergeMap(counters =>
        Object.keys(counters).map(label => ({
          label,
          filtered: false,
          nbRequests: +counters[label],
        }))
      )
    );
  }

  public getTotalCounter(): Observable<number> {
    let pullActive$ = this.agentMailFacade.pull$;
    // retrieve nb resquest by type
    let mailCommon$ = this.basketStore.pipe(fromMailBasket.selectLabelTotalCountByType(ChannelType.MAIL, EpticaRequestType.ASSIGNABLE));
    let mailPersonal$ = this.basketStore.pipe(fromMailBasket.selectLabelTotalCountByType(ChannelType.MAIL, EpticaRequestType.RELATED));
    let sum$ = combineLatest(mailCommon$, mailPersonal$).pipe(sumOfArr(), startWith(0));
    return pullActive$.pipe(switchMap(pull => (pull ? sum$ : mailPersonal$)));
  }

  public getTotalCounterForCsr(): Observable<number> {
    return this.basketStore.pipe(fromMailBasket.selectLabelCountByType(ChannelType.MAIL, EpticaRequestType.RELATED_FOR_SUP_CSR));
  }

  sortEventsByDateAndIdAscending(events: EpticaExposedRequestEvent[]): EpticaExposedRequestEvent[] {
    function compareEvents(eventA: EpticaExposedRequestEvent, eventB: EpticaExposedRequestEvent): number {
      const dateComparison = eventA.date - eventB.date;
      return dateComparison === 0 ? Number(eventA.id) - Number(eventB.id) : dateComparison;
    }

    return [...events].sort(compareEvents);
  }

  private toBasketRequest(source: MailRequest, drafts: EpticaExposedDraft[], csrSupRequested?: string): RequestModel {
    const { id, detail, customer } = source;
    const customerEmail = customer && extractEpticaField(customer, 'EMail');
    const skills = detail && extractEpticaField(detail, 'awlPublicSkills');
    const recipient = detail && extractEpticaField(detail, 'awlDestination');
    const memo = detail && extractEpticaField(detail, 'awlDifferMemo');
    const priority = detail && extractEpticaField(detail, 'awlWeight');
    const bookmark = detail && extractEpticaField(detail, 'wlPriorityFlag') === 'true';
    const reason = detail && extractEpticaField(detail, 'awlOwiSkill');
    const lastContact = detail && extractEpticaField(detail, 'wlLastContact');
    const hasInitialAttachment = detail && extractEpticaField(detail, 'awlHasAttachment') === 'true';
    const responsibleAccountId = detail && detail.accountResponsible && detail.accountResponsible.id;
    const accountId = (this.epticaDeprecatedService.user && this.epticaDeprecatedService.user.accountId) || '';
    const state = detail && detail.requestState;
    const draftUuid = detail && detail.draftUuid;
    const hasDraft = !id || (!!drafts && drafts.some(({ requestId }) => requestId === detail.id));
    const customerId = customer && extractEpticaField(customer, 'iasId');
    let previewContent = '';
    let sendDate: EpticaExposedRequestEvent | undefined;

    let lastEventDate = new Date();
    let forwardedMemo = {
      message: '',
      date: '',
    };

    if (detail.events && detail.events.length > 0) {
      const sortedEvents = this.sortEventsByDateAndIdAscending(detail.events);
      const lastEvent = sortedEvents[detail.events.length - 1];
      // Since eptica 9.7.1, text field is removed
      detail.events
        .filter(_event => _event.type === EpticaRequestEventType.CLIENT_MESSAGE)
        .forEach(_event => _event.message && (previewContent = _event.message.text || _event.message.html || ''));

      if (!previewContent) {
        const event = detail.events.find(_event => _event.type === EpticaRequestEventType.OUTGOING_EMAIL_SENT);
        previewContent = (event && event.message && (event.message.text || event.message.html)) || '';
      }

      if (lastEvent) {
        let eventDateInMs = lastEvent.date * 1000;
        lastEventDate = new Date(eventDateInMs);
        forwardedMemo = this.getForwardedMemoFromEvent(lastEvent);
      }
      // get send date for the agent's response
      sendDate = [...detail.events]
        .reverse()
        .find(ev => ev.type === EpticaRequestEventType.RESPONSE || ev.type === EpticaRequestEventType.OUTGOING_EMAIL_SENT);
    }

    const lastContactEmail = this.extractEmailAddress(lastContact);

    return {
      id,
      creationDate: detail.creationDate && (id || !draftUuid) ? new Date(detail.creationDate) : undefined,
      sendDate: sendDate?.message?.date ? new Date(sendDate?.message?.date) : undefined,
      object: detail.subject,
      content: detail.text || previewContent,
      hasAttachment: hasInitialAttachment,
      contactName: this.evaluateContactName(source),
      types: !id && !!draftUuid ? [EpticaRequestType.DRAFT] : this.evaluateRequestType(source, csrSupRequested),
      recipient,
      skillList: this.toSkills(skills),
      memo: this.toMemo(memo),
      priority: this.priorityFilter.toPriority(priority),
      selected: false,
      error: false,
      reason,
      state: state === 'closed' ? 'closed' : 'open',
      lastEventDate: lastEventDate,
      lastContactEmail: lastContactEmail,
      isLastContactPrincipalContact: this.isLastContactPrincipalContact(customerEmail, lastContactEmail),
      transferMemo: forwardedMemo,
      hasDraft: hasDraft,
      draftUuid,
      customerId,
      customerEmail,
      bookmark,
      isCurrentCsrResponsible: responsibleAccountId === accountId,
      read: detail.requestRead,
    };
  }

  public toBasketFilter(queryFilters: Partial<QueryFilterModel>): BasketFilter[] {
    return [
      new SubjectFilter(this.searchService, queryFilters.subject),
      new RequestIdFilter(queryFilters.requestId),
      new SenderFilter(queryFilters.domain),
      new RecipientFilter(queryFilters.recipient),
      new ReasonFilter(queryFilters.reason),
      new SkillsFilter(queryFilters.skills),
      new AttachmentFilter(queryFilters.attachmentEnabled, queryFilters.attachmentActive),
      this.priorityFilter.setValues(queryFilters.priorities || []),
      new BookmarkFilter(queryFilters.bookmark),
      new PeriodFilter(queryFilters.beginTime, queryFilters.endTime, queryFilters.isPeriodFilterEnabled),
    ];
  }

  private evaluateContactName(source: MailRequest): string {
    const { customer } = source;
    let customerFirstname = customer && extractEpticaField(customer, 'FirstName');
    customerFirstname = customerFirstname ? customerFirstname.trim().toLowerCase() : undefined;
    let customerLastname = customer && extractEpticaField(customer, 'LastName');
    customerLastname = customerLastname ? customerLastname.trim().toLowerCase() : undefined;
    const customerEmail = customer && extractEpticaField(customer, 'EMail');
    if (
      !!customerFirstname &&
      customerFirstname !== 'unknown' &&
      customerFirstname !== 'inconnu' &&
      customerFirstname !== '-' &&
      customerFirstname !== '?' &&
      !!customerLastname &&
      customerLastname !== 'unknown' &&
      customerLastname !== 'inconnu' &&
      customerLastname !== '-' &&
      customerLastname !== '?'
    ) {
      return `${capitalize(customerLastname)} ${capitalize(customerFirstname)}`;
    }
    if (!!customerEmail) {
      return `${customerEmail.toLowerCase()}`;
    }
    return 'unknown';
  }

  private evaluateRequestType(source: MailRequest, csrSupRequested?: string): EpticaRequestType[] {
    let types: EpticaRequestType[] = [];
    const { detail, askForHelpStatus } = source;
    const csrId = (this.epticaDeprecatedService.user && this.epticaDeprecatedService.user.accountId) || '';
    const responsibleAccountId = detail && detail.accountResponsible && detail.accountResponsible.id;
    const state = detail && detail.requestState;
    const forwarded = detail && detail.forwarded;
    const csrDedicated = detail && extractEpticaField(detail, 'awlCsrDedicated');
    const csrDedicatedInit = detail && extractEpticaField(detail, 'awlCsrInitDedica');
    const csrStatus = detail && extractEpticaField(detail, 'awlStatus');
    const lastRequestEventType = detail && detail.events && extractLastRequestEventType(detail.events);
    const lastRequestSendForReviewEvent =
      detail && detail.events && extractLastRequestEventDtoByType(detail.events, EpticaRequestEventType.REQUEST_REVIEW_REQUESTED);

    if (lastRequestEventType && lastRequestEventType === EpticaRequestEventType.REQUEST_OPENING) {
      types.push(EpticaRequestType.RE_OPENED);
    }

    // if dedicated csr is current agent
    if (
      (!detail.requestRead && !!csrDedicated && csrDedicated === this.userId) ||
      (!!csrDedicatedInit && csrDedicatedInit === this.userId)
    ) {
      types.push(EpticaRequestType.DEDICATED_AGENT_UNREAD);
    } else if (
      !detail.requestRead &&
      this.sessionDeprecatedService.isSupFeatureEnabled &&
      ((!!csrDedicated && csrDedicated === csrSupRequested) || (!!csrDedicatedInit && csrDedicatedInit === csrSupRequested))
    ) {
      types.push(EpticaRequestType.DEDICATED_AGENT_UNREAD);
    }
    // if state is review
    if (state === EpticaRequestState.review) {
      types.push(this.sessionDeprecatedService.isSupFeatureEnabled ? EpticaRequestType.APPROVAL : EpticaRequestType.APPROVAL_IN_PROCESS);
    }

    if (
      csrStatus === 'approval_rejected' &&
      lastRequestSendForReviewEvent &&
      responsibleAccountId &&
      lastRequestSendForReviewEvent.from.toString() === responsibleAccountId
    ) {
      types.push(EpticaRequestType.APPROVAL_REJECTED);
    }

    // if transfered to agent
    if (forwarded) {
      types.push(EpticaRequestType.TRANSFERED);
    }
    // if ask for help status is open
    // compute type with responsible
    if (askForHelpStatus.status === 'OPEN') {
      if (responsibleAccountId === csrId) {
        types.push(EpticaRequestType.ASK_FOR_HELP_SENT);
      } else {
        types.push(EpticaRequestType.ASK_FOR_HELP_RECEIVED);
      }
    }
    // if ask for help status is answered
    else if (askForHelpStatus.status === 'ANSWERED') {
      types.push(EpticaRequestType.ASK_FOR_HELP_ANSWERED);
    }

    if (types.length) {
      return types;
    }

    // otherwise give default type
    return [EpticaRequestType.RELATED];
  }

  private extractEmailAddress(contact: string): string | undefined {
    if (contact) {
      const beginChar = '<';
      const endChar = '>';
      if (contact.indexOf(beginChar) !== -1 && contact.indexOf(endChar) !== -1) {
        return contact.substring(contact.lastIndexOf(beginChar) + 1, contact.lastIndexOf(endChar));
      }

      if (contact.indexOf('@') !== -1 && contact.indexOf('.') !== -1) {
        return contact.trim();
      }
    }
    return undefined;
  }

  private isLastContactPrincipalContact(customerEmail: string | undefined, lastContactEmail: string | undefined): boolean {
    return !(customerEmail && lastContactEmail) || customerEmail === lastContactEmail;
  }

  private toSkills(skills: string): Skill[] {
    if (!!skills) {
      try {
        return JSON.parse(skills)
          .filter(skill => !skill.masked)
          .map(skill => ({
            id: skill.uuid,
            name: skill.name,
          }));
      } catch (_ignore) {}
    }
    return [];
  }

  private toMemo(memo: string): Memo {
    if (!!memo) {
      try {
        return JSON.parse(memo);
      } catch (_ignore) {}
    }
    return {
      message: '',
      date: '',
    };
  }

  private getForwardedMemoFromEvent(event: EpticaExposedRequestEvent): Memo {
    let forwardedMemo = {
      message: '',
      date: '',
    };

    try {
      let descriptionObject = JSON.parse(event.description);
      // get memo from transfer by skill or transfer by team
      if (descriptionObject && MailComment.isForwardType(descriptionObject.type)) {
        forwardedMemo = {
          message: descriptionObject.comment,
          date: new Date(descriptionObject.date).getTime().toString(),
        };
      }
    } catch (_ignore) {}

    // get memo from transfer to agent or to external mail
    if (
      event.type === EpticaRequestEventType.REQUEST_FORWARDED_WITH_ATTACHMENTS ||
      event.type === EpticaRequestEventType.REQUEST_FORWARDED_BY_AGENT_TO_EXTERNAL_SPECIALIST ||
      event.type === EpticaRequestEventType.REQUEST_FORWARDED
    ) {
      let eventDateInMs = event.date * 1000;
      forwardedMemo = {
        message: event.description,
        date: eventDateInMs.toString(),
      };
    }

    return forwardedMemo;
  }
  public saveFilters(filters: Partial<QueryFilterModel>): Observable<void> {
    let filtersStr = '';
    // if filters is not empty, we serialize the filter
    if (Object.keys(filters).length !== 0) {
      filtersStr = JSON.stringify(this.serializeFilter(filters));
    }

    return this.httpDeprecated
      .asObservable()
      .withData(filtersStr)
      .withConfig({ headers: { 'Content-Type': 'application/json' } })
      .post(`${MAIL_FACADE_PREFIX}/saveSearchRequestFilter/desktop`)
      .pipe(
        catchError(err => {
          this.logger.error(`Error while saving filter data (error: ${JSON.stringify(err)})`);
          this.toastDeprecatedService.alert('agent:toast.mail_filter_save_failed');
          return new Observable<void>();
        })
      );
  }

  saveSort(categories: BasketCategoryModel[]): Observable<void> {
    return this.httpDeprecated
      .asObservable()
      .withData(JSON.stringify(this.serializeSort(categories)))
      .withConfig({ headers: { 'Content-Type': 'application/json' } })
      .post(`${MAIL_FACADE_PREFIX}/saveSort/desktop`)
      .pipe(
        catchError(err => {
          this.logger.error(`Error while saving sort data (error: ${JSON.stringify(err)})`);
          this.toastDeprecatedService.alert('agent:toast.mail_sort_save_failed');
          return new Observable<void>();
        })
      );
  }

  public getFiltersAndSort(): Observable<MailBasketSortAndFilterDto> {
    // We add the deviceType to the request to get filter correctly
    return this.httpDeprecated
      .asObservable()
      .withConfig({ headers: { 'Content-Type': 'application/json' } })
      .get(`${MAIL_FACADE_PREFIX}/getFiltersAndSort/desktop`)
      .pipe(
        map(({ data }) => ({
          filterValue: (data.filterValue && JSON.parse(data.filterValue)) || {},
          sortValue: (data.sortValue && JSON.parse(data.sortValue)) || [],
        }))
      );
  }

  public overrideMailInitialState(initModel: MailChannelModel, result: MailBasketSortAndFilterDto): MailChannelModel {
    let newModel = initModel;

    // filters
    if (result.filterValue && Object.keys(result.filterValue).length > 0) {
      // tslint:disable-next-line:no-shadowed-variable
      const filter = this.deserializeFilter(result.filterValue);
      const skills = this.checkFilterSkillsAvailability(this.availableSkills, filter);
      newModel = {
        ...newModel,
        queryFilters: {
          ...newModel.queryFilters,
          ...filter,
          skills,
        },
      };
    }

    // sort
    if (result.sortValue) {
      result.sortValue.forEach(value => {
        newModel = {
          ...newModel,
          categories: {
            ...newModel.categories,
            entities: {
              ...newModel.categories.entities,
              [value.category]: {
                ...newModel.categories.entities[value.category],
                sort: {
                  ...newModel.categories.entities[value.category].sort,
                  key: value.sort[QueryFilterKeys.SORT_KEY],
                  order: value.sort[QueryFilterKeys.SORT_DIRECTION],
                },
              },
            },
          },
        };
      });
    }

    return newModel;
  }

  changeBookmark(requestId: string, bookmark: boolean): Observable<void> {
    return this.mailService.changeBookmark(requestId, { bookmark }).pipe(
      catchError(err => {
        this.$log.error(`Error while modifying bookmark (error: ${JSON.stringify(err)})`);
        this.toastDeprecatedService.alert('agent:toast.mail_change_bookmark_failed');
        return new Observable<void>();
      })
    );
  }

  /**
   * check if a skill from the loaded filter is not available anymore and update the filter in database
   * @param availableSkills available skills for the filter
   * @param queryFilterModel the loaded filter
   */
  private checkFilterSkillsAvailability(availableSkills: Chosen<Skill>[], queryFilterModel: QueryFilterModel): Skill[] {
    const availableSkillsId = availableSkills.map(skill => skill.id);
    const skills = queryFilterModel.skills || [];
    // test the difference between filter skills and available skills
    if (skills.filter(({ id }) => !availableSkillsId.includes(id)).length > 0) {
      this.logger.error(`A skill is not available anymore : we removed it`);
      this.toastDeprecatedService.alert('agent:toast.mail_filter_deleted_skill');

      const updatedSkills = skills.filter(({ id }) => availableSkillsId.includes(id));
      const updatedQueryFilterModel = {
        ...queryFilterModel,
        skills: updatedSkills,
      };
      this.saveFilters(updatedQueryFilterModel).subscribe();
      return updatedSkills;
    }
    return skills;
  }

  /**
   * Convert sort model to dto object
   */
  private serializeSort(categories: BasketCategoryModel[]): MailBasketSortDto[] {
    return categories.map(basket => ({
      category: basket.category,
      sort: {
        [QueryFilterKeys.SORT_KEY]: basket.sort.key,
        [QueryFilterKeys.SORT_DIRECTION]: basket.sort.order,
      },
    }));
  }

  /**
   * Convert filters model by removing unwanted filter fields
   */
  private serializeFilter(filters: Partial<QueryFilterModel>): MailBasketFilterDto {
    const keys = Object.keys(filters)
      .filter(k => EXCLUDED_FILTER_PROPS.indexOf(k) === -1)
      .filter(k => filters[k] !== undefined);
    return keys.reduce<MailBasketFilterDto>((result, key) => {
      const _key = this.serializeKey(key);
      if (!_key) {
        return result;
      }
      return {
        ...result,
        [_key]: this.serializeData(filters[key]),
      };
    }, {});
  }

  /**
   * Convert dto filter to front model
   */
  private deserializeFilter(dto: MailBasketFilterDto): QueryFilterModel {
    return {
      hasFilters: true,
      subject: dto[QueryFilterKeys.SUBJECT],
      domain: dto[QueryFilterKeys.DOMAIN],
      skills: dto[QueryFilterKeys.SKILLS] || [],
      attachmentEnabled: dto[QueryFilterKeys.ATTACHMENT_ENABLED],
      attachmentActive: dto[QueryFilterKeys.ATTACHMENT_ACTIVE],
      priorities: dto[QueryFilterKeys.PRIORITIES] || [],
      bookmark: dto[QueryFilterKeys.BOOKMARK],
      beginTime: new Date(dto[QueryFilterKeys.BEGIN_TIME]),
      endTime: new Date(dto[QueryFilterKeys.END_TIME]),
      isPeriodFilterEnabled: dto[QueryFilterKeys.IS_PERIOD_FILTER_ENABLED],
      recipient: dto[QueryFilterKeys.RECIPIENT],
      reason: dto[QueryFilterKeys.REASON],
      requestId: dto[QueryFilterKeys.REQUEST_ID],
    };
  }

  /**
   * adds a layer of mapping between the "front-end filter key" and the "database filter key"
   * so we can change one without changing the other
   */
  private serializeKey(key: string): QueryFilterKeys | undefined {
    switch (key) {
      case 'subject':
        return QueryFilterKeys.SUBJECT;
      case 'domain':
        return QueryFilterKeys.DOMAIN;
      case 'skills':
        return QueryFilterKeys.SKILLS;
      case 'attachmentEnabled':
        return QueryFilterKeys.ATTACHMENT_ENABLED;
      case 'attachmentActive':
        return QueryFilterKeys.ATTACHMENT_ACTIVE;
      case 'priorities':
        return QueryFilterKeys.PRIORITIES;
      case 'bookmark':
        return QueryFilterKeys.BOOKMARK;
      case 'beginTime':
        return QueryFilterKeys.BEGIN_TIME;
      case 'endTime':
        return QueryFilterKeys.END_TIME;
      case 'isPeriodFilterEnabled':
        return QueryFilterKeys.IS_PERIOD_FILTER_ENABLED;
      case 'recipient':
        return QueryFilterKeys.RECIPIENT;
      case 'reason':
        return QueryFilterKeys.REASON;
      case 'requestId':
        return QueryFilterKeys.REQUEST_ID;
      case 'key':
        return QueryFilterKeys.SORT_KEY;
      case 'order':
        return QueryFilterKeys.SORT_DIRECTION;
      default:
        return undefined;
    }
  }

  private serializeData(data: any): any {
    if (data === null || data === undefined) {
      return undefined;
    }
    if (isDate(data)) {
      return data.getTime();
    }
    return data;
  }
}

export default MODULE_NAME;
